// React & Libraries
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

// Ant Design
import {
  Tooltip, DatePicker, Alert, Tag, notification, Popover, Button
} from 'antd';
import {
  UserOutlined, LinkOutlined, InfoCircleOutlined, EditFilled, CheckOutlined, FileProtectOutlined, ExclamationCircleOutlined, LoadingOutlined
} from '@ant-design/icons';

// UI Libraries
import H3Text from '@Uilib/h3Text';
import H3FormInput from '@Uilib/h3FormInput';

// Helpers & Constants
import { toISTDate } from '@Apis/constants';
import Helpers from '@Apis/helpers';

// Common components
import ViewCustomFields from '@Components/Common/CustomField/ViewCustomFields';
import SelectPaymentTerm from '@Components/Common/SelectPaymentTerm';
import SelectAppUser from '@Components/Common/SelectAppUser';
import PRZSelect from '@Components/Common/UI/PRZSelect';
import ViewDocumentEwb from '@Components/Common/ViewDocumentEwb';

// File specific components
import PushIRPButtons from '../PushIRPButtons';

function ViewInvoiceDetails({
  props, state, updateState, quickUpdateDisable, isTallyConnected, accountingGSTTransactionAsPerMaster, cancellationReasonOptions
}) {

  const {
    getInvoiceById, user, isQuickView, selectedInvoice, quickUpdateInvoiceLoading, quickUpdateInvoice, getActivityLog, cancelIRP, cancelIRPLoading, pushIRPLoading, pushIRP
  } = props;

  const {
    isGstNumberEditable, updatedGstNumber, currentInvId, currentTenantId, updatedPaymentTerms, updateDueDate, isPaymentRemark, updatePaymentRemark, updateAccountManager, cancellationReasonIRP, cancellationRemark, showErrorIRN
  } = state;

  return (
    <Fragment>
      <div className="document-header">
        <div className="ant-row">
          <div className="ant-col-md-24">
            <H3Text
              text={(
                <span>
                  <UserOutlined />
                  {' '}
                  <span>{`${selectedInvoice?.internal_cust_code} - ${selectedInvoice?.customer_info?.customer_name}`}</span>
                </span>
              )}
              className="document-header__party-name"
            />
          </div>
          {(selectedInvoice?.sales_order_number && selectedInvoice?.order_type !== 'PACKING_SLIP' && Helpers.getPermission(Helpers.permissionEntities.SALES_ORDER, Helpers.permissionTypes.READ, user)) && (
            <div className="ant-col-md-12">

              <div className="document-header__field">
                <H3Text text="SO #" className="document-header__field-name" />

                <H3Text text={<Link to={`/sales/sales-order/view/${selectedInvoice?.order_id}`} target="_blank">{selectedInvoice?.sales_order_number || '-'}</Link>} className="document-header__field-value" />
              </div>
            </div>
          )}
          {(selectedInvoice?.sales_order_number && selectedInvoice?.order_type === 'PACKING_SLIP' && Helpers.getPermission(Helpers.permissionEntities.PACKING_SLIP, Helpers.permissionTypes.READ, user)) && (
            <div className="ant-col-md-12">

              <div className="document-header__field">
                <H3Text text="PS #" className="document-header__field-name" />

                <H3Text text={<Link to={`/sales/packing-slip/view/${selectedInvoice?.order_id}`} target="_blank">{selectedInvoice?.sales_order_number || '-'}</Link>} className="document-header__field-value" />
              </div>
            </div>
          )}
          <div className="ant-col-md-12">
            <Tooltip title={selectedInvoice?.tenant_info?.tenant_name}>
              <div className="document-header__field">
                <H3Text text="Location" className="document-header__field-name" />
                <H3Text text={selectedInvoice?.tenant_info?.tenant_name?.length > 25 ? `${selectedInvoice?.tenant_info?.tenant_name?.substring(0, 25)}...` : selectedInvoice?.tenant_info?.tenant_name} className="document-header__field-value" />
              </div>
            </Tooltip>
          </div>
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Department" className="document-header__field-name" />
              <H3Text text={selectedInvoice?.tenant_department_info?.department_name} className="document-header__field-value" />
            </div>
          </div>
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Invoice Date" className="document-header__field-name" />
              <H3Text
                text={selectedInvoice?.invoice_date ? (dayjs(selectedInvoice.invoice_date).format('DD/MM/YYYY')) : '-'}
                className="document-header__field-value"
              />
            </div>
          </div>
          <div className="ant-col-md-12">
            <div className="document-header__field document-header__dueDate">
              <H3Text text="Due Date" className="document-header__field-name" />
              {quickUpdateDisable
                ? (
                  <div className="document-header__field-input">
                    <DatePicker
                      value={updateDueDate}
                      onChange={(value) => {
                        updateState({ updateDueDate: value });
                        quickUpdateInvoice({
                          invoice_due_date: value || [],
                          invoice_id: selectedInvoice.invoice_id,
                        }, () => {
                          updateState({ isUserReadyForIssueStock: true });
                          getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                        }, () => {
                          updateState({ isUserReadyForIssueStock: false });
                        });
                      }}
                      format="DD/MM/YYYY"
                      allowClear={false}
                    />
                  </div>
                )
                : (
                  <H3Text
                    text={selectedInvoice?.invoice_due_date ? (dayjs(selectedInvoice.invoice_due_date).format('DD/MM/YYYY')) : '-'}
                    className="document-header__field-value"
                  />
                )}
            </div>
          </div>
          {selectedInvoice?.conversion_rate && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Conversion Rate" className="document-header__field-name" />
                <H3Text text={`${selectedInvoice?.conversion_rate}`} className="document-header__field-value" />
              </div>
            </div>
          )}
          {isTallyConnected && !selectedInvoice?.tally_warning_message && selectedInvoice?.tally_updated_at
            && (
              <div className="ant-col-md-12">
                <div className="document-header__field">
                  <H3Text text="Tally Status" className="document-header__field-name" />
                  <H3Text
                    text={`Pushed on ${toISTDate(selectedInvoice?.tally_updated_at).format('DD MMM, YYYY hh:mm A')}`}
                    className="document-header__field-value"
                  />
                </div>
              </div>
            )}
          {!selectedInvoice?.tally_updated_at && isTallyConnected && !selectedInvoice?.tally_warning_message
            && (
              <div className="ant-col-md-12">
                <div className="document-header__field">
                  <H3Text text="Tally Status" className="document-header__field-name" />
                  <H3Text
                    text="Not Synced"
                    className="document-header__field-value"
                  />
                </div>
              </div>
            )}
          {selectedInvoice?.tally_warning_message && isTallyConnected
            && (
              <div className="ant-col-md-12">
                <div className="document-header__field">
                  <H3Text text="Tally Status" className="document-header__field-name" />
                  <H3Text
                    text={(
                      <React.Fragment>
                        <span
                          className="status-tag"
                          style={{
                            backgroundColor: 'rgb(255, 0, 0)',
                          }}
                        >
                          Error
                        </span>
                        &nbsp;
                        <Tooltip
                          title={selectedInvoice?.tally_warning_message}
                        >
                          <InfoCircleOutlined style={{ color: 'rgb(255, 0, 0)', cursor: 'pointer' }} />
                        </Tooltip>
                      </React.Fragment>
                    )}
                    className="document-header__field-value"
                  />
                </div>
              </div>
            )}
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Payment Term" className="document-header__field-name" />
              <SelectPaymentTerm
                selectedPaymentTerm={updatedPaymentTerms}
                onChange={(value) => {
                  updateState({ updatedPaymentTerms: value?.due_days });
                  const paymentTerms = [
                    {
                      due_days: value?.due_days,
                    },
                  ];
                  quickUpdateInvoice({
                    payment_terms: paymentTerms || [],
                    invoice_id: selectedInvoice.invoice_id,
                  }, () => {
                    updateState({ isUserReadyForIssueStock: true });
                    getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                  }, () => {
                    updateState({ isUserReadyForIssueStock: false });
                  });
                }}
                callback={(value) => {
                  updateState({ updatedPaymentTerms: Number(value) });
                }}
                placeholder="Select Payment Term"
                hideTitle
                inputClassName="document-header__field-input"
                disabled={!quickUpdateDisable}
              />
            </div>
          </div>
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Payment Remark" className="document-header__field-name" />
              {(isPaymentRemark) && (
                <div className="document-header__field-input">
                  <input
                    value={updatePaymentRemark}
                    onChange={(e) => {
                      updateState({ updatePaymentRemark: e.target.value });
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        updateState({ isPaymentRemark: false });
                        const paymentTerms = [
                          {
                            remark: updatePaymentRemark,
                          },
                        ];
                        quickUpdateInvoice({
                          payment_terms: paymentTerms || [],
                          invoice_id: selectedInvoice.invoice_id,
                        }, () => {
                          updateState({ isUserReadyForIssueStock: true });
                          getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                        }, () => {
                          updateState({ isUserReadyForIssueStock: false });
                        });
                      }
                    }}
                    className="document-custom-input"
                  />
                  &nbsp;
                  <CheckOutlined
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => {
                      updateState({ isPaymentRemark: false });
                      const paymentTerms = [
                        {
                          remark: updatePaymentRemark,
                        },
                      ];
                      quickUpdateInvoice({
                        payment_terms: paymentTerms || [],
                        invoice_id: selectedInvoice.invoice_id,
                      }, () => {
                        updateState({ isUserReadyForIssueStock: true });
                        getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                      }, () => {
                        updateState({ isUserReadyForIssueStock: false });
                      });
                    }}
                  />
                </div>
              )}
              {(!isPaymentRemark) && (
                <div className="document-header__field-value">
                  {updatePaymentRemark && (
                    <H3Text text={updatePaymentRemark} />
                  )}
                  {quickUpdateDisable && (
                    <EditFilled
                      className="edit-icon"
                      onClick={() => {
                        updateState({ isPaymentRemark: true });
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          {selectedInvoice?.seller_info && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Sold By" className="document-header__field-name" />
                <H3Text
                  text={<a href={`/vendors/view/${selectedInvoice?.seller_info?.seller_id}`} target="_blank" rel="noreferrer">{`#${selectedInvoice?.seller_info?.internal_slr_code} - ${selectedInvoice?.seller_info?.seller_name?.toProperCase()}`}</a>}
                  className="document-header__field-value"
                />
              </div>
            </div>
          )}
          {selectedInvoice?.stock_status && selectedInvoice?.status === 'CONFIRMED' && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Fulfillment Status" className="document-header__field-name" />
                <div style={{ color: Helpers.getStatusColor(selectedInvoice?.stock_status).color }}>
                  {selectedInvoice.stock_status?.replaceAll('_', ' ') || '-'}
                </div>
              </div>
            </div>
          )}
          {!isQuickView && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="GSTIN" className="document-header__field-name" />
                {(isGstNumberEditable) && (
                  <div className="document-header__field-input">
                    <input
                      value={updatedGstNumber}
                      onChange={(e) => {
                        updateState({ updatedGstNumber: e.target.value });
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          updateState({ isGstNumberEditable: false, isHsnEditable: false });
                          quickUpdateInvoice({
                            gst_number: updatedGstNumber || null,
                            invoice_id: selectedInvoice.invoice_id,
                          }, () => {
                            getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                            updateState({ isHsnEditable: true });
                          }, updateState({ isHsnEditable: false }));
                        }
                      }}
                      className="document-custom-input"
                      maxLength="15"
                    />
                    &nbsp;
                    <CheckOutlined
                      style={{ color: '#1890ff', cursor: 'pointer' }}
                      onClick={() => {
                        updateState({ isGstNumberEditable: false, isHsnEditable: false });
                        quickUpdateInvoice({
                          gst_number: updatedGstNumber || null,
                          invoice_id: selectedInvoice.invoice_id,
                        }, () => getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10), updateState({ isHsnEditable: false }));
                      }}
                    />
                  </div>
                )}
                {(!isGstNumberEditable) && (
                  <div className="document-header__field-value">
                    {updatedGstNumber && (
                      <H3Text text={updatedGstNumber} />
                    )}
                    {!selectedInvoice?.shopify_order_id && !selectedInvoice?.unicommerce_invoice_code && !selectedInvoice?.irn && !selectedInvoice?.e_way_bills && quickUpdateDisable && !accountingGSTTransactionAsPerMaster && (
                      <EditFilled
                        className="edit-icon"
                        onClick={() => {
                          updateState({ isGstNumberEditable: true });
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {selectedInvoice?.price_list_details && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Price List" className="document-header__field-name" />
                <H3Text
                  text={selectedInvoice?.price_list_details?.price_list_name || '-'}
                  className="document-header__field-value"
                />
              </div>
            </div>
          )}

          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Sales Manager" className="document-header__field-name" />
              <SelectAppUser
                hideTitle
                selectedUser={updateAccountManager}
                onChange={(value) => {
                  updateState({ updateAccountManager: value });
                  quickUpdateInvoice({
                    account_manager_id: value || null,
                    invoice_id: selectedInvoice.invoice_id,
                  }, () => {
                    updateState({ isUserReadyForIssueStock: true });
                    getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                  }, () => {
                    updateState({ isUserReadyForIssueStock: false });
                  });
                }}
                disabled={!quickUpdateDisable}
                containerClassName="document-header__field-input"
              />
            </div>
          </div>

          {
            user?.tenant_info?.zoho_branch_id && selectedInvoice?.zoho_invoice_id && (
              <div className="ant-col-md-12">
                <div className="document-header__field">
                  <H3Text text="Zoho Status" className="document-header__field-name" />
                  <H3Text
                    text={(
                      <a href={`https://books.zoho.in/app#/invoices/${selectedInvoice?.zoho_invoice_id}`} target="_blank" rel="noreferrer">
                        <span className="status-tag">Pushed</span>
                        <LinkOutlined />
                      </a>
                    )}
                    className="document-header__field-value"
                  />
                </div>
              </div>
            )
          }
          {
            selectedInvoice?.integration_busy_config?.status === 'CONNECTED' && ['CONFIRMED'].includes(selectedInvoice?.status) && (
              <div className="ant-col-md-12">
                <div className="document-header__field">
                  <H3Text text="Busy Status" className="document-header__field-name" />
                  {selectedInvoice?.busy_voucher_id && (
                    <H3Text
                      text={(
                        <React.Fragment>
                          <span className="status-tag">Pushed</span>
                        </React.Fragment>
                      )}
                      className="document-header__field-value"
                    />
                  )}
                  {selectedInvoice?.busy_sync_error && (
                    <H3Text
                      text={(
                        <React.Fragment>
                          <span
                            className="status-tag"
                            style={{
                              backgroundColor: 'rgb(255, 0, 0)',
                            }}
                          >
                            Error
                          </span>
                          &nbsp;
                          <Tooltip
                            title={selectedInvoice?.busy_sync_error}
                          >
                            <InfoCircleOutlined style={{ color: 'rgb(255, 0, 0)', cursor: 'pointer' }} />
                          </Tooltip>
                        </React.Fragment>
                      )}
                      className="document-header__field-value"
                    />
                  )}
                  {(!selectedInvoice?.busy_voucher_id && !selectedInvoice?.busy_sync_error) && (
                    <H3Text
                      text={(
                        <React.Fragment>
                          <span
                            className="status-tag"
                            style={{
                              backgroundColor: 'rgb(70, 139, 247)',
                            }}
                          >
                            Not Synced
                          </span>
                        </React.Fragment>
                      )}
                      className="document-header__field-value"
                    />
                  )}
                </div>
              </div>
            )
          }
          {selectedInvoice?.integration_busy_config?.status === 'CONNECTED' && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Busy STPT Account" className="document-header__field-name" />
                <div className="document-header__field-input">
                  <PRZSelect
                    showSearch
                    placeholder="Select Account"
                    value={selectedInvoice?.busy_stpt_name}
                    options={selectedInvoice?.integration_busy_config?.sales_type_master_list.map((__k) => ({
                      value: __k.name,
                      label: __k.name,
                    }))}
                    style={{
                      width: 170,
                    }}
                    disabled={!quickUpdateDisable}
                    loading={quickUpdateInvoiceLoading}
                    size="small"
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    onChange={(value) => {
                      quickUpdateInvoice({
                        busy_stpt_name: value,
                        invoice_id: selectedInvoice.invoice_id,
                      });
                      getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                    }}
                    className="document-header__field-value document-header__field-dropdown"
                  />
                </div>
              </div>
            </div>
          )}
          {(selectedInvoice?.tenant_id === user?.tenant_info?.tenant_id && user?.tenant_info?.it_id) && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Tally sales Account" className="document-header__field-name" />
                <div className="document-header__field-input">
                  <PRZSelect
                    showSearch
                    placeholder="Select Account"
                    value={selectedInvoice?.tally_sales_account_name}
                    options={user?.tenant_info?.tally_configuration?.sales_account_list?.map((__k) => ({
                      value: __k.sales_account_name,
                      label: __k.sales_account_name,
                    }))}
                    style={{
                      width: 170,
                    }}
                    // disabled={!quickUpdateDisable}
                    loading={quickUpdateInvoiceLoading}
                    size="small"
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    onChange={(value) => {
                      quickUpdateInvoice({
                        tally_sales_account_name: value,
                        invoice_id: selectedInvoice.invoice_id,
                      });
                      getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                    }}
                    className="document-header__field-value document-header__field-dropdown"
                  />
                </div>
              </div>
            </div>
          )}
          {(selectedInvoice?.tenant_id === user?.tenant_info?.tenant_id && user?.tenant_info?.it_id && selectedInvoice?.integration_tally_config?.sales_voucher_type_selection === 'DOC_LEVEL') && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Tally Voucher Type" className="document-header__field-name" />
                <div className="document-header__field-input">
                  <PRZSelect
                    showSearch
                    placeholder="select voucher type"
                    value={selectedInvoice?.sales_voucher_type}
                    options={selectedInvoice?.integration_tally_config?.voucher_types?.map((__k) => ({
                      value: __k.voucher_type_name,
                      label: __k.voucher_type_name,
                    }))}
                    style={{
                      width: 170,
                    }}
                    disabled={!quickUpdateDisable}
                    loading={quickUpdateInvoiceLoading}
                    size="small"
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    onChange={(value) => {
                      quickUpdateInvoice({
                        sales_voucher_type: value,
                        invoice_id: selectedInvoice.invoice_id,
                      });
                      getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10);
                    }}
                    className="document-header__field-value document-header__field-dropdown"
                  />
                </div>
              </div>
            </div>
          )}
          {selectedInvoice?.unicommerce_so_code && !isQuickView && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Unicommerce" className="document-header__field-name" />
                <H3Text
                  text={(
                    <a href={`https://ptinventindia.unicommerce.com/order/shipments?orderCode=${selectedInvoice?.unicommerce_so_code}&shipmentCode=${selectedInvoice?.unicommerce_shipping_package_code}`} target="_blank" rel="noreferrer">
                      <span className="status-tag">Pushed</span>
                      <LinkOutlined />
                    </a>
                  )}
                  className="document-header__field-value"
                />
              </div>
            </div>
          )}
          {selectedInvoice?.shopify_order_id && !isQuickView && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text
                  text="Shopify Order#"
                  className="document-header__field-name"
                />
                <div className="document-header__field-value">
                  <a
                    style={{
                      color: 'rgb(45, 125, 247)',
                    }}
                    href={`https://admin.shopify.com/store/${selectedInvoice?.shopify_shop_name?.split('.')?.[0]}/orders/${selectedInvoice?.shopify_order_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selectedInvoice?.shopify_order_number}
                  </a>

                </div>
              </div>
            </div>
          )}
          {(selectedInvoice?.cancellation_reason && selectedInvoice?.status === 'VOID') && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Cancel Reason" className="document-header__field-name" />
                <H3Text
                  text={selectedInvoice?.cancellation_reason || '-'}
                  className="document-header__field-value"
                />
              </div>
            </div>
          )}
          {selectedInvoice?.subcontractor_mos?.length > 0 && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Sub Contractor MO" className="document-header__field-name" />
                <H3Text
                  text={
                    selectedInvoice?.subcontractor_mos?.length == 1 ? (
                      <Link to={`/production/manufacturing-orders/view/${selectedInvoice?.subcontractor_mos[0]?.mo_id}`} key={selectedInvoice?.subcontractor_mos[0]?.mo_id} target='_blank' style={{ 'color': '#2D7DF7' }}>
                        {selectedInvoice?.subcontractor_mos[0]?.mo_number}
                      </Link>
                    ) : (
                      <Tooltip
                        overlay={(
                          <div
                            style={{
                              color: 'rgb(0, 0, 0)',
                              fontSize: '12px',
                              fontWeight: '500',
                              padding: '2px',
                            }}
                          >
                            {selectedInvoice?.subcontractor_mos?.map(item => {
                              <Link to={`/production/manufacturing-orders/view/${item?.mo_id}`} key={item?.mo_id} target='_blank' style={{ 'color': '#2D7DF7' }}>{item?.mo_number}</Link>
                            })}
                          </div>
                        )}
                        color="rgb(234, 242, 254)"
                        overlayStyle={{ zIndex: 9999 }}
                      >
                        {`${selectedInvoice?.subcontractor_mos[0]?.mo_number}... +${selectedInvoice?.subcontractor_mos?.length - 1} more`}
                      </Tooltip>
                    )
                  }
                  className="document-header__field-value"
                />
              </div>
            </div>
          )}
          {(selectedInvoice?.custom_fields?.length > 0 && !isQuickView) ? (
            <ViewCustomFields
              customFields={selectedInvoice?.custom_fields?.filter((cf) => cf.is_active)}
              wrapperClass="document-header__field"
              labelClass="document-header__field-name"
              valueClass="document-header__field-value"
              inputClassName="document-header__field-input"
              isHorizontal
              isEditable={quickUpdateDisable}
              handleCustomFieldUpdate={quickUpdateInvoice}
              entityId={selectedInvoice?.invoice_id}
              callback={() => getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId)}
              entityName="invoice_id"
              entityType='INVOICE'
            />
          ) : ''}
        </div>
      </div>
      {
        user?.tenant_info?.sales_config?.addons?.e_invoicing?.is_active && Helpers.getPermission(Helpers.permissionEntities.E_INVOICE, Helpers.permissionTypes.READ, user) && user.user_tenants.find((tenant) => tenant.tenant_id === user.tenant_info.tenant_id)?.is_e_way_bill_enabled && !['SENT_FOR_APPROVAL', 'DRAFT', 'VOID'].includes(selectedInvoice?.status) && (
          <div className="view-invoice__e-inv-info" style={{ marginTop: '5px' }}>
            {selectedInvoice?.e_invoice_status === 'PUSHED' && selectedInvoice?.e_canceled_date === null && (
              <Alert
                className="e-invoice-alert-pushed"
                message={(
                  <React.Fragment>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div>
                          <span>
                            <FileProtectOutlined
                              style={{
                                color: '#52c41a',
                                marginRight: '5px',
                              }}
                            />
                            {' '}
                          </span>
                          e-lnvoice
                          {' '}
                          <Tag color="#87d068">PUSHED</Tag>
                          {' '}
                          An IRN and a QR code has been generated
                          {' '}
                          <span
                            className="copy-irn"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedInvoice?.irn);
                              notification.success({
                                message: 'IRN Copied',
                                placement: 'top',
                                duration: 4,
                              });
                            }}
                          >
                            Copy IRN
                          </span>
                        </div>
                        {selectedInvoice?.e_invoice_errors?.length > 0 && selectedInvoice?.e_invoice_errors[0]?.length > 0
                          && (
                            <div>
                              <div className="e-invoice-error-messages">

                                {selectedInvoice?.e_invoice_errors?.map((error, index) => (
                                  <div
                                    key={index}
                                    className="e-invoice-error-message"
                                    style={{
                                      backgroundColor: '#fff1f0',
                                    }}
                                  >
                                    <span>
                                      <ExclamationCircleOutlined
                                        style={{
                                          color: '#ff4d4f',
                                          marginRight: '5px',
                                        }}
                                      />
                                      {' '}
                                    </span>
                                    {error}
                                  </div>
                                ))}
                              </div>
                            </div>

                          )}
                      </div>
                      {Helpers.getPermission(Helpers.permissionEntities.E_INVOICE, Helpers.permissionTypes.UPDATE, user) && (
                        <Popover
                          placement="topRight"
                          trigger="click"
                          title={<div style={{ width: '280px' }}>Are you sure to cancel this e-Invoice?</div>}
                          content={(
                            <React.Fragment>
                              <div style={{ marginBottom: '10px' }}>
                                <label className="orgFormLabel">
                                  Reason for cancellation
                                  <span style={{ color: 'red' }}> *</span>
                                </label>
                                <PRZSelect
                                  placeholder="Select a reason"
                                  onChange={(value) => updateState({ cancellationReasonIRP: value })}
                                  dropdownStyle={{ zIndex: 9999 }}
                                  value={cancellationReasonIRP}
                                  options={cancellationReasonOptions}
                                />
                              </div>
                              <H3FormInput
                                name="cancellation remark"
                                type="text"
                                label="Remarks"
                                containerClassName="orgInputContainer"
                                inputClassName="orgFormInput"
                                labelClassName="orgFormLabel"
                                placeholder=""
                                onChange={(event) => updateState({ cancellationRemark: event.target.value })}
                                value={cancellationRemark}
                                maxlength="50"
                                required
                                showError={showErrorIRN === 'VOID' && !cancellationRemark}
                              />
                              <div className="IRN__cancellation-buttons">
                                <Button
                                  onClick={() => {
                                    updateState({ showErrorIRN: 'VOID' });
                                    if (cancellationRemark && cancellationReasonIRP) {
                                      updateState({ cancellationRemarkPop: false });
                                      cancelIRP({
                                        irn: selectedInvoice?.irn,
                                        tenant_id: selectedInvoice?.tenant_id,
                                        invoice_id: currentInvId,
                                        cancel_reason: cancellationReasonIRP,
                                        cancel_remark: cancellationRemark,
                                      }, () => {
                                        updateState({ cancellationRemark: '', cancellationReasonIRP: '', showErrorIRN: '' });
                                        getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                                      });
                                    }
                                  }}
                                  type="primary"
                                  size="small"
                                  loading={cancelIRPLoading}
                                >
                                  Confirm
                                </Button>
                              </div>
                            </React.Fragment>
                          )}
                          disabled={cancelIRPLoading}
                        >
                          <div
                            className="push-to-irp__cancel-button"
                            onClick={() => {
                              updateState({ cancellationRemarkPop: true });
                            }}
                          >
                            {cancelIRPLoading ? <LoadingOutlined /> : 'Cancel'}
                          </div>
                        </Popover>
                      )}
                    </div>
                  </React.Fragment>
                )}
                type="success"
              />
            )}
            {
              (
                selectedInvoice?.e_invoice_status === 'FAILED' && selectedInvoice?.e_canceled_date === null ? (
                  <PushIRPButtons
                    pushIRPLoading={pushIRPLoading}
                    pushIRP={() => pushIRP([{ invoice_id: currentInvId }], () => getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId))}
                    selectedInvoice={selectedInvoice}
                    type="error"
                    tagValue="FAILED"
                    tagColor="#ff4d4f"
                    iconColor="#ff4d4f"
                    message="Pushing to the IRP failed either due to incomplete data or portal issues"
                  />
                ) : selectedInvoice?.e_invoice_status === null && selectedInvoice?.e_canceled_date === null ? (
                  <PushIRPButtons
                    pushIRPLoading={pushIRPLoading}
                    pushIRP={() => pushIRP([{ invoice_id: currentInvId }], () => getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId))}
                    selectedInvoice={selectedInvoice}
                    type="warning"
                    tagValue="YET TO BE PUSHED"
                    tagColor="rgb(236, 149, 0)"
                    iconColor="rgb(236, 149, 0)"
                    message="Push this invoice to the IRP to generate an IRN"
                  />
                ) : selectedInvoice?.e_canceled_date ? (
                  <Alert
                    className="e-invoice-alert-pushed"
                    message={(
                      <div>
                        <span>
                          <FileProtectOutlined
                            style={{
                              color: '#ff4d4f',
                              marginRight: '5px',
                            }}
                          />
                        </span>
                        e-lnvoice
                        {' '}
                        <Tag color="#ff4d4f">CANCELED</Tag>
                        on
                        {' '}
                        {selectedInvoice?.e_canceled_date && toISTDate(selectedInvoice?.e_canceled_date).utc().format('DD/MM/YYYY hh:mm A')}
                        .
                        <br />
                        Subsequent generation of an e-Invoice is no longer possible for this particular invoice.
                      </div>
                    )}
                    type="warning"
                  />
                ) : null
              )
            }
          </div>
        )
      }
      {
        selectedInvoice?.e_way_bills?.length && (
          <ViewDocumentEwb
            eWayBills={selectedInvoice?.e_way_bills}
            actionCallback={() => getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId)}
          />
        )
      }
      {/* {
        selectedInvoice?.e_way_bills?.length && (
          <TransporterDetailsEWB
            eWayBills={selectedInvoice?.e_way_bills}
            actionCallback={() => getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId)}
          />
        )
      } */}
    </Fragment>
  );
};

export default ViewInvoiceDetails;